import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { RosterPilotLogo } from '../../assets/RosterPilotLogo';
import { requestPasswordReset } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';
import './ForgotPassword.css';

type ForgotPasswordFormData = {
  email: string;
};

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apiError, setApiError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  useEffect(() => {
    if (user) {
      navigate('/players', { replace: true });
    }
  }, [user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    mode: 'onSubmit',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setApiError('');
    setIsSubmitting(true);

    try {
      await requestPasswordReset(data.email);
      setSubmittedEmail(data.email);
      setEmailSent(true);
    } catch (error: unknown) {
      const authError = error as { code?: string; message?: string };
      let errorMessage = 'Failed to send password reset email';

      switch (authError.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          errorMessage = authError.message || errorMessage;
      }

      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="forgot-password-screen">
        <div className="forgot-password-container">
          <div className="forgot-password-logo">
            <RosterPilotLogo />
          </div>

          <div className="forgot-password-header">
            <h1 className="forgot-password-title">Check Your Email</h1>
            <p className="forgot-password-subtitle">
              We've sent a password reset link to <strong>{submittedEmail}</strong>
            </p>
          </div>

          <div className="forgot-password-success">
            <p>Click the link in the email to reset your password. If you don't see the email, check your spam folder.</p>
          </div>

          <div className="forgot-password-actions">
            <Link to="/login" className="forgot-password-back-link">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-screen">
      <div className="forgot-password-container">
        <div className="forgot-password-logo">
          <RosterPilotLogo />
        </div>

        <div className="forgot-password-header">
          <h1 className="forgot-password-title">Reset Password</h1>
          <p className="forgot-password-subtitle">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        <form className="forgot-password-form" onSubmit={handleSubmit(onSubmit)}>
          {apiError && (
            <div className="forgot-password-error-banner">{apiError}</div>
          )}

          <div className="forgot-password-inputs">
            <TextInput
              label="Email Address"
              type="email"
              placeholder="email@example.com"
              autoComplete="username"
              error={errors.email?.message}
              disabled={isSubmitting}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Please enter a valid email address',
                },
              })}
            />
          </div>

          <div className="forgot-password-actions">
            <Button type="submit" fullWidth loading={isSubmitting}>
              Send Reset Link
            </Button>

            <Link to="/login" className="forgot-password-back-link">
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};
